import { useContext } from 'react'
import Box from '@mui/material/Box'
import type { FormNode } from '../../../../types/form.types'
import { GridColumnsContext } from './gridColumnsContext'

export interface FormGridRowProps {
  nodes: FormNode[]
  renderNode: (node: FormNode) => React.ReactNode
  /** MUI spacing factor for grid gap (HTML mock uses 16px → 2). */
  gap?: number
  alignItems?: 'center' | 'flex-start' | 'stretch'
  /** App tabular row: eat leftover height so the grid, not the form, scrolls. */
  grow?: boolean
}

export function FormGridRow({ nodes, renderNode, gap = 1, alignItems = 'center', grow = false }: FormGridRowProps) {
  const fixedColumns = useContext(GridColumnsContext)
  const colCount = Math.max(fixedColumns ?? 1, ...nodes.map((n) => n.col + n.colSpan))
  return (
    <Box
      data-form-grid-row
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${colCount}, 1fr)`,
        gap,
        alignItems,
        width: '100%',
        ...(grow
          ? { flex: 1, minHeight: 0, overflow: 'hidden', height: '100%' }
          : null),
      }}
    >
      {nodes.map((node) => (
        <Box
          key={node.id}
          sx={{
            gridColumn: `${node.col + 1} / span ${node.colSpan}`,
            width: node.width === 'auto' ? '100%' : node.width,
            minWidth: 0,
            ...(grow && (node.type === 'Table' || node.type === 'Pages')
              ? {
                  minHeight: 0,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }
              : null),
          }}
        >
          {renderNode(node)}
        </Box>
      ))}
    </Box>
  )
}
